import * as React from "react"
import { createPortal } from "react-dom"

const Select = ({ children, value, onValueChange }) => {
  const [open, setOpen] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 })
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null)

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both container and dropdown portal
      if (containerRef.current && !containerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  React.useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [open])

  // Find the selected item's text
  const content = React.Children.toArray(children).find(child => child?.type === SelectContent)
  const items = content ? React.Children.toArray(content.props.children).filter(Boolean) : []
  const selectedItem = items.find(item => item?.props?.value === value)
  const displayText = selectedItem?.props?.children || value || 'Select...'

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(!open)
        }}
        className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
      >
        <span className="truncate">{displayText}</span>
        <svg className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {open && createPortal(
        <div 
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999
          }}
          className="rounded-md border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto"
        >
          {React.Children.toArray(children).map((child, idx) => {
            if (child?.type === SelectContent) {
              return React.cloneElement(child, { 
                key: idx,
                value, 
                onValueChange: (newValue) => {
                  onValueChange(newValue)
                  setOpen(false)
                }
              })
            }
            return null
          })}
        </div>,
        document.body
      )}
    </div>
  )
}

const SelectTrigger = ({ children }) => {
  return <>{children}</>
}

const SelectValue = ({ children }) => {
  return <>{children}</>
}

const SelectContent = ({ children, value, onValueChange }) => {
  return (
    <>
      {React.Children.map(children, (child, idx) => {
        if (child && child.type === SelectItem) {
          return React.cloneElement(child, {
            key: idx,
            value,
            onValueChange,
            isSelected: child.props.value === value
          })
        }
        return child
      })}
    </>
  )
}

const SelectItem = ({ children, value: itemValue, onValueChange, isSelected }) => {
  return (
    <div
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onValueChange(itemValue)
      }}
      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${
        isSelected ? 'bg-slate-50 font-medium' : ''
      }`}
    >
      {children}
    </div>
  )
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem }
